import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { mediaSrc } from "@/lib/media";

export const PhotoGallery = ({ images, featured, testid }) => {
  const ref = useRef(null);
  const lbRef = useRef(null);
  const [idx, setIdx] = useState(0);
  const [lbOpen, setLbOpen] = useState(false);
  const [lbIdx, setLbIdx] = useState(0);
  const h = featured ? "h-[26rem]" : "h-72";
  const go = (i) => {
    const n = Math.max(0, Math.min(images.length - 1, i));
    const el = ref.current;
    if (el) el.scrollTo({ left: el.clientWidth * n, behavior: "smooth" });
    setIdx(n);
  };
  const onScroll = () => {
    const el = ref.current;
    if (el) setIdx(Math.round(el.scrollLeft / el.clientWidth));
  };
  const openLb = (i) => { setLbIdx(i); setLbOpen(true); };
  const lbGo = (i) => {
    const n = Math.max(0, Math.min(images.length - 1, i));
    const el = lbRef.current;
    if (el) el.scrollTo({ left: el.clientWidth * n, behavior: "smooth" });
    setLbIdx(n);
  };
  const onLbScroll = () => {
    const el = lbRef.current;
    if (el) setLbIdx(Math.round(el.scrollLeft / el.clientWidth));
  };
  useEffect(() => {
    if (lbOpen && lbRef.current) {
      lbRef.current.scrollLeft = lbRef.current.clientWidth * lbIdx;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lbOpen]);
  return (
    <div className="relative group" data-testid={testid}>
      <div
        ref={ref} onScroll={onScroll}
        className="flex overflow-x-auto snap-x snap-mandatory rounded-xl [&::-webkit-scrollbar]:hidden"
        style={{ scrollbarWidth: "none" }}
      >
        {images.map((u, i) => (
          <img key={i} src={mediaSrc(u)} alt={`Photo ${i + 1}`} onClick={() => openLb(i)} data-testid={`gallery-thumb-${i}`} className={`w-full shrink-0 snap-center object-cover rounded-xl cursor-zoom-in ${h}`} />
        ))}
      </div>
      {images.length > 1 && (
        <>
          <button
            type="button" aria-label="Previous photo" onClick={() => go(idx - 1)} disabled={idx === 0}
            className="absolute left-2 top-1/2 -translate-y-1/2 grid place-items-center w-9 h-9 rounded-full bg-white/85 text-foreground shadow-md backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-0"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button" aria-label="Next photo" onClick={() => go(idx + 1)} disabled={idx === images.length - 1}
            className="absolute right-2 top-1/2 -translate-y-1/2 grid place-items-center w-9 h-9 rounded-full bg-white/85 text-foreground shadow-md backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-0"
          >
            <ChevronRight size={18} />
          </button>
          <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
            {images.map((_, i) => (
              <button
                key={i} type="button" aria-label={`Go to photo ${i + 1}`} onClick={() => go(i)}
                className={`h-1.5 rounded-full transition-all ${i === idx ? "w-5 bg-white" : "w-1.5 bg-white/60"}`}
              />
            ))}
          </div>
        </>
      )}

      <AnimatePresence>
        {lbOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] bg-black/90 backdrop-blur-sm flex items-center justify-center"
            onClick={() => setLbOpen(false)} data-testid="lightbox"
          >
            <button
              type="button" aria-label="Close" onClick={() => setLbOpen(false)} data-testid="lightbox-close"
              className="absolute top-5 right-5 z-10 grid place-items-center w-11 h-11 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            >
              <X size={20} />
            </button>
            <div className="absolute top-6 left-1/2 -translate-x-1/2 text-white/80 text-sm z-10">{lbIdx + 1} / {images.length}</div>
            <div
              ref={lbRef} onScroll={onLbScroll} onClick={(e) => e.stopPropagation()}
              className="flex w-full h-full overflow-x-auto snap-x snap-mandatory [&::-webkit-scrollbar]:hidden items-center"
              style={{ scrollbarWidth: "none" }}
            >
              {images.map((u, i) => (
                <div key={i} className="w-full h-full shrink-0 snap-center flex items-center justify-center p-6 sm:p-12">
                  <img src={mediaSrc(u)} alt={`Photo ${i + 1}`} className="max-w-full max-h-full object-contain rounded-lg" />
                </div>
              ))}
            </div>
            {images.length > 1 && (
              <>
                <button
                  type="button" aria-label="Previous" onClick={(e) => { e.stopPropagation(); lbGo(lbIdx - 1); }} disabled={lbIdx === 0}
                  className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 grid place-items-center w-11 h-11 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors disabled:opacity-30"
                >
                  <ChevronLeft size={22} />
                </button>
                <button
                  type="button" aria-label="Next" onClick={(e) => { e.stopPropagation(); lbGo(lbIdx + 1); }} disabled={lbIdx === images.length - 1}
                  className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 grid place-items-center w-11 h-11 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors disabled:opacity-30"
                >
                  <ChevronRight size={22} />
                </button>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
